import OpenAI from 'openai';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

export async function getEmbedding(text) {
  if (!process.env.REACT_APP_OPENAI_API_KEY) {
    throw new Error('OpenAI API key is not configured');
  }

  try {
    const response = await openai.embeddings.create({
      model: "text-embedding-3-small",
      input: text,
    });

    return response.data[0].embedding;
  } catch (error) {
    console.error('Error generating embedding:', error);
    throw error;
  }
}

export function calculateCosineSimilarity(embedding1, embedding2) {
  if (!embedding1 || !embedding2) return 0;
  
  // Calculate dot product
  const dotProduct = embedding1.reduce((sum, value, i) => sum + value * embedding2[i], 0);
  
  // Calculate magnitudes
  const magnitude1 = Math.sqrt(embedding1.reduce((sum, value) => sum + value * value, 0));
  const magnitude2 = Math.sqrt(embedding2.reduce((sum, value) => sum + value * value, 0));
  
  // Calculate cosine similarity
  if (magnitude1 === 0 || magnitude2 === 0) return 0;
  return dotProduct / (magnitude1 * magnitude2);
}

export function createSimilarityMatrix(notes) {
  if (!notes || notes.length === 0) return [];
  
  // Initialize matrix with zeros
  const matrix = Array(notes.length).fill(0).map(() => Array(notes.length).fill(0));
  
  // Calculate similarities
  for (let i = 0; i < notes.length; i++) {
    for (let j = i; j < notes.length; j++) {
      if (i === j) {
        matrix[i][j] = 1; // Same note has similarity of 1
        continue;
      }
      
      const similarity = calculateCosineSimilarity(
        notes[i].embedding,
        notes[j].embedding
      );
      
      // Matrix is symmetric
      matrix[i][j] = similarity;
      matrix[j][i] = similarity;
    }
  }
  
  return matrix;
}

// Helper function to flatten 2D array into object
function flattenMatrix(matrix) {
  const flattened = {};
  matrix.forEach((row, i) => {
    row.forEach((value, j) => {
      flattened[`${i}_${j}`] = value;
    });
  });
  return {
    size: matrix.length,
    values: flattened
  };
}

// Helper function to unflatten object back to 2D array
function unflattenMatrix(flatMatrix) {
  if (!flatMatrix || !flatMatrix.values || !flatMatrix.size) return [];
  
  const matrix = Array(flatMatrix.size).fill(0)
    .map(() => Array(flatMatrix.size).fill(0));
  
  Object.entries(flatMatrix.values).forEach(([key, value]) => {
    const [i, j] = key.split('_').map(Number);
    matrix[i][j] = value;
  });
  
  return matrix;
}

export async function storeSimilarityMatrix(userId, matrix) {
  try {
    // console.log('🔥 Firebase: Setting similarity matrix doc for user', userId);
    const flatMatrix = flattenMatrix(matrix);
    await setDoc(doc(db, "similarityMatrices", userId), {
      matrix: flatMatrix,
      lastUpdated: new Date(),
    });
  } catch (error) {
    console.error("Error storing similarity matrix:", error);
    throw error;
  }
}

export async function getSimilarityMatrix(userId) {
  try {
    // console.log('🔥 Firebase: Getting similarity matrix doc for user', userId);
    const matrixDoc = await getDoc(doc(db, "similarityMatrices", userId));
    if (matrixDoc.exists()) {
      const data = matrixDoc.data();
      return unflattenMatrix(data.matrix);
    }
    return null;
  } catch (error) {
    console.error("Error retrieving similarity matrix:", error);
    throw error;
  }
}

export async function generateSummary(text) {
  if (!process.env.REACT_APP_OPENAI_API_KEY) {
    throw new Error('OpenAI API key is not configured');
  }

  try {
    const response = await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: "Extract key insights from the article as bullet points. Omit any preamble and fluff."
        },
        {
          role: "user",
          content: `Please summarize the following article:\n\n${text}`
        }
      ],
      temperature: 0.7,
      max_tokens: 150
    });

    return response.choices[0].message.content;
  } catch (error) {
    console.error('Error generating summary:', error);
    throw error;
  }
} 